import {
  Badge,
  Button,
  Flex,
  FormatDate,
  Icon,
  Modal,
  Table,
  Text,
  Title,
  Tooltip,
} from '@wyz/ui';
import { DateUtils } from '@wyz/utils';
import { useState } from 'react';
import { Form } from '@wyz/ui';
import { Trans, useI18n, useLocale } from '~/common/context';
import { ICartItem } from '~/purchase/types';

type Props = {
  onClose: () => void;
  onSubmit: (comment: string | undefined) => void;
  items: ICartItem[];
  comment: string | undefined;
  isLoading: boolean;
};

function NotOrderableSupplierModal({
  items,
  comment: commentDefaultValue,
  onSubmit,
  onClose,
  isLoading,
}: Props) {
  const { t } = useI18n();
  const [comment, setComment] = useState<string | undefined>(
    commentDefaultValue,
  );
  const locale = useLocale();
  return (
    <Modal.Container open={true} onClose={onClose} size='lg'>
      <Modal.Header variant='white'>
        <Title heading='lg' weight='bold'>
          <Trans i18nKey='app.purchase.cart_page.not_invoiceable_suppliers.title' />
        </Title>
      </Modal.Header>
      <Modal.Body>
        <Text heading='normal' color='primary'>
          <Trans i18nKey='app.purchase.cart_page.not_invoiceable_suppliers.description' />
        </Text>
        <div className='bg-body-secondary rounded border my-3'>
          <Table>
            <tr>
              <td>
                <Text color='secondary-gray' weight='semi-bold'>
                  <Trans i18nKey='app.purchase.cart_page.not_invoiceable_suppliers.source' />
                </Text>
              </td>
              <td>
                <Text color='secondary-gray' weight='semi-bold'>
                  <Trans i18nKey='app.common.quantity' />
                </Text>
              </td>
              <td>
                <Text color='secondary-gray' weight='semi-bold'>
                  <Trans i18nKey='app.common.estimation_delivery' />
                </Text>
              </td>
              <td>
                <Text color='secondary-gray' weight='semi-bold'>
                  <Trans i18nKey='app.purchase.cart_page.not_invoiceable_suppliers.price' />
                </Text>
              </td>
              <td>
                <Text color='secondary-gray' weight='semi-bold'>
                  <Trans i18nKey='app.common.status' />
                </Text>
              </td>
            </tr>
            {items.map((item) => (
              <tr key={item.id}>
                <td>
                  <Text weight='semi-bold' heading='normal'>
                    {item.supplier.name}
                  </Text>
                </td>
                <td>
                  <Text weight='semi-bold' heading='normal'>
                    {item.quantity}
                  </Text>
                </td>
                <td>
                  <Text weight='semi-bold' heading='normal'>
                    <FormatDate
                      date={item.delivery.date}
                      format={DateUtils.DATE_FORMAT.DM}
                    />
                  </Text>
                </td>
                <td>
                  <Text weight='semi-bold' heading='normal'>
                    {locale.formatters.money(item.price.unitPrice, {
                      currency: item.price.currency,
                    })}
                  </Text>
                </td>
                <td>
                  <Flex alignItems='center' className='gap-2'>
                    <Text weight='semi-bold' heading='normal'>
                      <Badge bg='warning' pill>
                        <Trans i18nKey='app.common.status.not_orderable' />
                      </Badge>
                    </Text>
                    <Tooltip
                      label={
                        <Trans i18nKey='app.purchase.redistribution.pendin_order.accept.not_orderable_tooltip' />
                      }
                      style={{ maxWidth: '300px' }}
                    >
                      <Text component='span' color='danger'>
                        <Icon icon='error-warning-line' />
                      </Text>
                    </Tooltip>
                  </Flex>
                </td>
              </tr>
            ))}
          </Table>
        </div>
        <div className='bg-body-secondary p-3 rounded border mb-3'>
          <Title heading='lg' weight='bold' className='mb-2'>
            <Trans i18nKey='app.purchase.orders.table.comment' />
          </Title>
          <Form.Control
            type='text'
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            as='textarea'
            placeholder={t('app.purchase.cart_page.comment')}
          />
        </div>
        <Button
          id='submit'
          variant='primary'
          fullWidth
          isLoading={isLoading}
          onClick={() => onSubmit(comment)}
        >
          <Trans i18nKey='app.common.button.i_understand' />
        </Button>
      </Modal.Body>
    </Modal.Container>
  );
}

export default NotOrderableSupplierModal;
